import { useEffect, useState, useCallback } from 'react';
import { useRouter, useParams } from 'next/navigation';
import Link from 'next/link';
import {
  ArrowLeft, Loader2, AlertTriangle, Save, Plus, ChevronRight,
  Lock, Unlock, Trophy, Users, Check, Trash2, Upload, ImageIcon, Package, Truck
} from 'lucide-react';
import { cn, localDatetimeToISO, isoToLocalDatetime } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { authFetch } from '@/lib/auth-client';
import { API_BASE } from '@/lib/api';
import { uploadAdminImage } from '@/lib/upload';
import { Button } from '@/components/ui/button';

const SPORTS = ['NBA', 'NCAA_BASKETBALL', 'NHL', 'SOCCER_EPL', 'SOCCER_WORLD_CUP', 'SOCCER_UCL'];
const EVENT_TYPES = ['BRACKET', 'ROUND_ROBIN', 'HEAD_TO_HEAD', 'SWISS'];
const STATUSES = ['UPCOMING', 'OPEN', 'LOCKED', 'COMPLETED', 'ARCHIVED'];
const UNDERCARD_STATUSES = ['UPCOMING', 'OPEN', 'LOCKED', 'SCORING', 'COMPLETED'];

const STATUS_COLORS: Record<string, string> = {
  UPCOMING: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  OPEN: 'bg-green-500/10 text-green-400 border-green-500/30',
  LOCKED: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  SCORING: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  COMPLETED: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
};

interface PrizeRow {
  placement: number;
  prizeType: 'COINS' | 'ITEM';
  coinAmount: number;
  itemName: string;
  itemImageUrl: string;
}

interface EventData {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  sport: string;
  status: string;
  accessType: string;
  eventType: string;
  externalValidationUrl: string | null;
  startTime: string;
  endTime: string;
  entryFee: number;
  prizePool: number;
  prizeType: string;
  prizeDistribution: Record<string, number> | null;
  prizes?: Array<{
    id: string;
    placement: number;
    prizeType: string;
    coinAmount: number;
    itemName: string | null;
    itemImageUrl: string | null;
    itemSku: string | null;
  }>;
  undercardCount: number;
  entryCount: number;
}

interface AccessCodeData {
  id: string;
  code: string;
  redeemedBy: string | null;
  redeemedAt: string | null;
  createdAt: string;
}

interface UndercardData {
  id: string;
  title: string;
  round: number;
  status: string;
  pointsPerCorrectPick: number;
  eventType: string;
  lockTime: string;
  startTime: string;
  endTime: string | null;
  gameCount: number;
  propCount: number;
}

export default function AdminMainEventDetailPage() {
  const router = useRouter();
  const { accessToken } = useAuthStore();
  // Migrated from Next.js: original code awaited a Promise `params` prop.
  // React Router gives us :id synchronously via useParams.
  const { id: eventIdParam } = useParams<{ id: string }>();
  const [eventId, setEventId] = useState(eventIdParam ?? '');
  const [event, setEvent] = useState<EventData | null>(null);
  const [undercards, setUndercards] = useState<UndercardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showAddUndercard, setShowAddUndercard] = useState(false);
  const [shiftDays, setShiftDays] = useState<number>(7);
  const [shifting, setShifting] = useState(false);
  const [settling, setSettling] = useState(false);

  // Edit form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    imageUrl: '',
    sport: 'NCAA_BASKETBALL',
    status: 'UPCOMING',
    accessType: 'OPEN',
    eventType: 'BRACKET',
    externalValidationUrl: '',
    startTime: '',
    endTime: '',
    entryFee: 0,
  });

  // Prizes state
  const [prizes, setPrizes] = useState<PrizeRow[]>([]);

  // Prize claims state (for ITEM fulfillment)
  interface ClaimRow {
    id: string; rank: number; status: string;
    shippingName: string | null; addressLine1: string | null;
    city: string | null; state: string | null; zip: string | null;
    trackingNumber: string | null;
    user: { username: string; email: string };
    mainEventPrize: { itemName: string | null; itemImageUrl: string | null; placement: number };
  }
  const [prizeClaims, setPrizeClaims] = useState<ClaimRow[]>([]);
  const [trackingInputs, setTrackingInputs] = useState<Record<string, string>>({});

  // Access codes state
  const [accessCodes, setAccessCodes] = useState<AccessCodeData[]>([]);
  const [codesInput, setCodesInput] = useState('');
  const [uploadingCodes, setUploadingCodes] = useState(false);

  // New undercard form
  const [ucForm, setUcForm] = useState({
    title: '',
    round: 0,
    pointsPerCorrectPick: 1,
    eventType: 'PICK_EM',
    lockTime: '',
    startTime: '',
    endTime: '',
  });

  useEffect(() => {
    if (eventIdParam) setEventId(eventIdParam);
  }, [eventIdParam]);

  const fetchData = useCallback(async () => {
    if (!eventId || !accessToken) return;
    setLoading(true);

    try {
      // Fetch event detail — use the admin list and find by id, or use the public detail
      const [eventRes, ucRes] = await Promise.all([
        fetch(`${API_BASE}/api/main-events/${eventId}`),
        authFetch(`/api/admin/main-events?status=`),
      ]);

      if (eventRes.ok) {
        const data = await eventRes.json();
        setEvent(data);
        setForm({
          title: data.title,
          description: data.description || '',
          imageUrl: data.imageUrl || '',
          sport: data.sport,
          status: data.status,
          accessType: data.accessType || 'OPEN',
          eventType: data.eventType || 'BRACKET',
          externalValidationUrl: data.externalValidationUrl || '',
          startTime: toDatetimeLocal(data.startTime),
          endTime: toDatetimeLocal(data.endTime),
          entryFee: data.entryFee,
        });
        // Populate prizes from event data
        if (data.prizes && data.prizes.length > 0) {
          setPrizes(data.prizes.map((p: NonNullable<EventData['prizes']>[0]) => ({
            placement: p.placement,
            prizeType: p.prizeType as 'COINS' | 'ITEM',
            coinAmount: p.coinAmount,
            itemName: p.itemName || '',
            itemImageUrl: p.itemImageUrl || '',
          })));
        } else if (data.prizeDistribution) {
          // Convert legacy JSON distribution to prize rows
          setPrizes(
            Object.entries(data.prizeDistribution as Record<string, number>)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([rank, amount]) => ({
                placement: parseInt(rank) || 1,
                prizeType: 'COINS' as const,
                coinAmount: amount,
                itemName: '',
                itemImageUrl: '',
              }))
          );
        }
        // Undercards come from the event detail
        if (data.undercards) {
          setUndercards(data.undercards);
        }

        // Fetch access codes if code-gated
        if (data.accessType === 'CODE_REQUIRED') {
          const codesRes = await authFetch(`/api/admin/main-events/${eventId}/access-codes`);
          if (codesRes.ok) {
            const codesData = await codesRes.json();
            setAccessCodes(codesData.data || codesData);
          }
        }

        // Fetch prize claims for completed events with ITEM prizes
        const hasItemPrizes = data.prizes?.some((p: { prizeType: string }) => p.prizeType === 'ITEM');
        if (data.status === 'COMPLETED' && hasItemPrizes) {
          const claimsRes = await authFetch(`/api/admin/main-events/${eventId}/prize-claims`);
          if (claimsRes.ok) {
            const claimsData = await claimsRes.json();
            setPrizeClaims(claimsData.data || claimsData);
          }
        }
      }
    } catch {
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [eventId, accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toDatetimeLocal = (iso: string) => isoToLocalDatetime(iso);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value,
    }));
  };

  const handleSave = async () => {
    if (!accessToken) return;
    setSaving(true);
    setError('');

    try {
      // Build prizes payload
      const prizesPayload = prizes
        .filter((p) => (p.prizeType === 'COINS' && p.coinAmount > 0) || (p.prizeType === 'ITEM' && p.itemName))
        .map((p) => ({
          placement: p.placement,
          prizeType: p.prizeType,
          coinAmount: p.prizeType === 'COINS' ? p.coinAmount : 0,
          itemName: p.prizeType === 'ITEM' ? p.itemName : null,
          itemImageUrl: p.prizeType === 'ITEM' && p.itemImageUrl ? p.itemImageUrl : null,
        }));

      const res = await authFetch(`/api/admin/main-events/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          imageUrl: form.imageUrl || null,
          externalValidationUrl: form.externalValidationUrl || null,
          startTime: localDatetimeToISO(form.startTime),
          endTime: localDatetimeToISO(form.endTime),
          prizes: prizesPayload,
        }),
      });

      if (res.ok) {
        setSuccessMsg('Event updated');
        setTimeout(() => setSuccessMsg(''), 3000);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to update');
      }
    } catch {
      setError('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleUcChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setUcForm((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value,
    }));
  };

  const handleAddUndercard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;
    setError('');

    try {
      const res = await authFetch(`/api/admin/main-events/${eventId}/undercards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...ucForm,
          lockTime: localDatetimeToISO(ucForm.lockTime),
          startTime: localDatetimeToISO(ucForm.startTime),
          endTime: ucForm.endTime ? localDatetimeToISO(ucForm.endTime) : undefined,
        }),
      });

      if (res.ok) {
        setShowAddUndercard(false);
        setUcForm({ title: '', round: 0, pointsPerCorrectPick: 1, eventType: 'PICK_EM', lockTime: '', startTime: '', endTime: '' });
        setSuccessMsg('Undercard added');
        setTimeout(() => setSuccessMsg(''), 3000);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to add undercard');
      }
    } catch {
      setError('Failed to add undercard');
    }
  };

  const handleDeleteUndercard = async (ucId: string, ucTitle: string) => {
    if (!confirm(`Delete "${ucTitle}"? This will permanently remove all games, picks, props, and entries for this undercard.`)) return;
    if (!accessToken) return;

    try {
      const res = await authFetch(`/api/admin/main-events/${eventId}/undercards/${ucId}`, {
        method: 'DELETE',
      });

      if (res.ok) {
        setUndercards((prev) => prev.filter((uc) => uc.id !== ucId));
        setSuccessMsg('Undercard deleted');
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to delete undercard');
      }
    } catch {
      setError('Failed to delete undercard');
    }
  };

  const handleShiftAllUndercards = async () => {
    if (!accessToken || shifting) return;
    if (undercards.length === 0) return;
    if (!Number.isFinite(shiftDays) || shiftDays === 0) {
      setError('Shift days must be a non-zero number');
      return;
    }
    if (
      !confirm(
        `Shift all ${undercards.length} undercards by ${shiftDays > 0 ? '+' : ''}${shiftDays} day(s)? This updates start, lock, and end times.`
      )
    ) {
      return;
    }
    setShifting(true);
    setError('');
    try {
      const ms = shiftDays * 24 * 60 * 60 * 1000;
      let succeeded = 0;
      const failures: string[] = [];
      for (const uc of undercards) {
        const body = {
          startTime: new Date(new Date(uc.startTime).getTime() + ms).toISOString(),
          lockTime: new Date(new Date(uc.lockTime).getTime() + ms).toISOString(),
          endTime: uc.endTime ? new Date(new Date(uc.endTime).getTime() + ms).toISOString() : null,
        };
        const res = await authFetch(`/api/admin/main-events/${eventId}/undercards/${uc.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          succeeded++;
        } else {
          failures.push(uc.title);
        }
      }
      if (failures.length === 0) {
        setSuccessMsg(`Shifted ${succeeded} undercards by ${shiftDays > 0 ? '+' : ''}${shiftDays} day(s)`);
        setTimeout(() => setSuccessMsg(''), 4000);
      } else {
        setError(`Shifted ${succeeded}/${undercards.length}; failed: ${failures.join(', ')}`);
      }
      // Refresh from server
      const detailRes = await authFetch(`/api/main-events/${eventId}`);
      if (detailRes.ok) {
        const data = await detailRes.json();
        setUndercards(data.undercards);
      }
    } catch {
      setError('Failed to shift undercards');
    } finally {
      setShifting(false);
    }
  };

  const handleSettleEvent = async () => {
    if (!confirm('Settle this entire event? This will rank all entries. This cannot be undone.')) return;
    if (!accessToken) return;

    setSettling(true);
    setError('');

    try {
      const res = await authFetch(`/api/admin/main-events/settle-event/${eventId}`, {
        method: 'POST',
      });

      if (res.ok) {
        setSuccessMsg('Event settled and ranked!');
        setTimeout(() => setSuccessMsg(''), 5000);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to settle event');
      }
    } catch {
      setError('Failed to settle event');
    } finally {
      setSettling(false);
    }
  };

  const handleUploadCodes = async () => {
    if (!accessToken || !codesInput.trim()) return;
    setUploadingCodes(true);
    setError('');

    const codes = codesInput.split('\n').map((c) => c.trim()).filter(Boolean);
    try {
      const res = await authFetch(`/api/admin/main-events/${eventId}/access-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes }),
      });

      if (res.ok) {
        const data = await res.json();
        setSuccessMsg(`Uploaded ${data.data?.uploaded ?? data.uploaded} codes`);
        setTimeout(() => setSuccessMsg(''), 3000);
        setCodesInput('');
        // Refresh codes list
        const codesRes = await authFetch(`/api/admin/main-events/${eventId}/access-codes`);
        if (codesRes.ok) {
          const codesData = await codesRes.json();
          setAccessCodes(codesData.data || codesData);
        }
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to upload codes');
      }
    } catch {
      setError('Failed to upload codes');
    } finally {
      setUploadingCodes(false);
    }
  };

  const handleShipPrize = async (claimId: string) => {
    const tracking = trackingInputs[claimId]?.trim();
    if (!tracking) {
      setError('Enter a tracking number');
      return;
    }

    try {
      const res = await authFetch(`/api/admin/main-events/${eventId}/prize-claims/${claimId}/ship`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trackingNumber: tracking }),
      });

      if (res.ok) {
        setSuccessMsg('Prize marked as shipped');
        setTimeout(() => setSuccessMsg(''), 3000);
        setPrizeClaims((prev) =>
          prev.map((c) => c.id === claimId ? { ...c, status: 'SHIPPED', trackingNumber: tracking } : c)
        );
        setTrackingInputs((prev) => {
          const next = { ...prev };
          delete next[claimId];
          return next;
        });
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to ship prize');
      }
    } catch {
      setError('Failed to ship prize');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 text-red-400 animate-spin" />
      </div>
    );
  }

  if (!event) {
    return <p className="text-zinc-400 py-12 text-center">Event not found</p>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          href="/admin/main-events"
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{event.title}</h1>
          <p className="text-sm text-zinc-400">
            {event.undercardCount ?? undercards.length} rounds · {event.entryCount ?? 0} entries
          </p>
        </div>
        <div className="flex gap-2">
          {event.status !== 'COMPLETED' && (
            <Button
              variant="destructive"
              onClick={handleSettleEvent}
              disabled={settling}
            >
              {settling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trophy className="h-4 w-4 mr-1" />}
              Settle Event
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {successMsg && (
        <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-3">
          <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
          <p className="text-green-400">{successMsg}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Event Edit Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Event Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-400 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none [color-scheme:dark]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-400 mb-1">Description</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none resize-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-400 mb-1">Banner Image URL <span className="text-zinc-600 font-normal">(1200×400 recommended)</span></label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    name="imageUrl"
                    value={form.imageUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/banner.jpg"
                    className="flex-1 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const ogUrl = `${API_BASE || window.location.origin}/api/og/event/${eventId}`;
                      handleChange({ target: { name: 'imageUrl', value: ogUrl } } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium whitespace-nowrap transition-colors"
                  >
                    Generate
                  </button>
                </div>
                {form.imageUrl && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-zinc-700">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={form.imageUrl}
                      alt="Banner preview"
                      className="w-full h-32 object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                )}
                {!form.imageUrl && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
                    <ImageIcon className="h-3.5 w-3.5" />
                    Displayed as hero banner on the event page
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Sport</label>
                <select
                  name="sport"
                  value={form.sport}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none [color-scheme:dark]"
                >
                  {SPORTS.map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Event Type</label>
                <select
                  name="eventType"
                  value={form.eventType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none [color-scheme:dark]"
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Status</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none [color-scheme:dark]"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Start Time</label>
                <input
                  type="datetime-local"
                  name="startTime"
                  value={form.startTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">End Time</label>
                <input
                  type="datetime-local"
                  name="endTime"
                  value={form.endTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Fee (coins)</label>
                <input
                  type="number"
                  name="entryFee"
                  value={form.entryFee}
                  onChange={handleChange}
                  min={0}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Access Type</label>
                <select
                  name="accessType"
                  value={form.accessType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none [color-scheme:dark]"
                >
                  <option value="OPEN">Open</option>
                  <option value="CODE_REQUIRED">Code Required</option>
                </select>
              </div>
              {form.accessType === 'CODE_REQUIRED' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-zinc-400 mb-1">External Validation URL</label>
                  <input
                    type="url"
                    name="externalValidationUrl"
                    value={form.externalValidationUrl}
                    onChange={handleChange}
                    placeholder="https://partner-api.com/validate-code"
                    className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    If set, codes are validated via POST to this URL. Otherwise, upload codes below.
                  </p>
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save Changes
              </Button>
            </div>
          </div>

          {/* Access Codes Section */}
          {form.accessType === 'CODE_REQUIRED' && !event.externalValidationUrl && (
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                Access Codes
                <span className="text-xs text-zinc-500 font-normal ml-2">
                  {accessCodes.filter((c) => !c.redeemedBy).length} available / {accessCodes.length} total
                </span>
              </h2>

              {/* Upload codes */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-zinc-400 mb-1">Paste codes (one per line) or upload a CSV</label>
                <textarea
                  value={codesInput}
                  onChange={(e) => setCodesInput(e.target.value)}
                  rows={4}
                  placeholder={"PRIME-X7K9\nPRIME-A3B2\nPRIME-M4N8"}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-red-500 focus:outline-none resize-none font-mono"
                />
                <div className="flex items-center justify-between mt-2">
                  <label className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors cursor-pointer">
                    <Upload className="h-3.5 w-3.5" />
                    <span>Import CSV</span>
                    <input
                      type="file"
                      accept=".csv,.txt"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          const text = ev.target?.result as string;
                          // Parse CSV: split by newlines, then by commas, flatten, trim, dedupe
                          const codes = text
                            .split(/\r?\n/)
                            .flatMap((line) => line.split(','))
                            .map((c) => c.trim().replace(/^["']|["']$/g, ''))
                            .filter(Boolean);
                          const unique = [...new Set(codes)];
                          setCodesInput((prev) => {
                            const existing = prev.trim();
                            return existing ? `${existing}\n${unique.join('\n')}` : unique.join('\n');
                          });
                        };
                        reader.readAsText(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <Button size="sm" onClick={handleUploadCodes} disabled={uploadingCodes || !codesInput.trim()}>
                    {uploadingCodes ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                    Upload Codes
                  </Button>
                </div>
              </div>

              {/* Codes table */}
              {accessCodes.length > 0 && (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-zinc-700">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-zinc-800">
                      <tr>
                        <th className="text-left px-3 py-2 text-zinc-400 font-medium">Code</th>
                        <th className="text-left px-3 py-2 text-zinc-400 font-medium">Status</th>
                        <th className="text-left px-3 py-2 text-zinc-400 font-medium">Redeemed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-700">
                      {accessCodes.map((code) => (
                        <tr key={code.id}>
                          <td className="px-3 py-2 text-white font-mono text-xs">{code.code}</td>
                          <td className="px-3 py-2">
                            {code.redeemedBy ? (
                              <span className="text-xs text-zinc-500">Used</span>
                            ) : (
                              <span className="text-xs text-green-400">Available</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-500">
                            {code.redeemedAt ? new Date(code.redeemedAt).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Undercards Section */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Undercards / Rounds</h2>
              <div className="flex items-center gap-3">
                {undercards.length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-zinc-500">Shift all by</span>
                    <input
                      type="number"
                      value={shiftDays}
                      onChange={(e) => setShiftDays(Number(e.target.value))}
                      className="w-14 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-white text-xs focus:border-amber-500 focus:outline-none"
                    />
                    <span className="text-zinc-500">day(s)</span>
                    <button
                      onClick={handleShiftAllUndercards}
                      disabled={shifting}
                      className="px-2 py-1 rounded text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 disabled:opacity-50 transition-colors"
                    >
                      {shifting ? '…' : 'Shift'}
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setShowAddUndercard(!showAddUndercard)}
                  className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Undercard
                </button>
              </div>
            </div>

            {/* Add undercard form */}
            {showAddUndercard && (
              <form onSubmit={handleAddUndercard} className="mb-6 p-4 bg-zinc-800 rounded-lg border border-zinc-700 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-zinc-400 mb-1">Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={ucForm.title}
                      onChange={handleUcChange}
                      required
                      placeholder="e.g., Round of 64"
                      className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-400 mb-1">Round (sort order) *</label>
                    <input
                      type="number"
                      name="round"
                      value={ucForm.round}
                      onChange={handleUcChange}
                      min={0}
                      className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-400 mb-1">Points per correct pick *</label>
                    <input
                      type="number"
                      name="pointsPerCorrectPick"
                      value={ucForm.pointsPerCorrectPick}
                      onChange={handleUcChange}
                      min={1}
                      className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-400 mb-1">Lock Time *</label>
                    <input
                      type="datetime-local"
                      name="lockTime"
                      value={ucForm.lockTime}
                      onChange={handleUcChange}
                      required
                      className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-400 mb-1">Start Time *</label>
                    <input
                      type="datetime-local"
                      name="startTime"
                      value={ucForm.startTime}
                      onChange={handleUcChange}
                      required
                      className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-zinc-400 mb-1">End Time</label>
                    <input
                      type="datetime-local"
                      name="endTime"
                      value={ucForm.endTime}
                      onChange={handleUcChange}
                      className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">
                      When games are expected to finish. Used by auto-settle cron — leave blank to require manual settlement.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddUndercard(false)}
                    className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <Button type="submit" size="sm">
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>
              </form>
            )}

            {/* Undercard list */}
            {undercards.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4 text-center">No undercards yet</p>
            ) : (
              <div className="space-y-2">
                {undercards
                  .sort((a, b) => a.round - b.round)
                  .map((uc) => (
                    <div
                      key={uc.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-zinc-800 hover:bg-zinc-750 border border-zinc-700 hover:border-zinc-600 transition-colors group"
                    >
                      <Link
                        href={`/admin/main-events/${eventId}/undercards/${uc.id}`}
                        className="flex items-center gap-3 flex-1 min-w-0"
                      >
                        <span className="w-6 h-6 rounded bg-zinc-700 flex items-center justify-center text-xs font-bold text-zinc-400">
                          {uc.round}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-white">{uc.title}</p>
                          <p className="text-xs text-zinc-500">
                            {uc.gameCount ?? 0} games · {uc.propCount ?? 0} props · {uc.pointsPerCorrectPick} points/pick
                          </p>
                        </div>
                      </Link>
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'px-2 py-0.5 rounded text-[10px] font-bold border',
                            STATUS_COLORS[uc.status] || STATUS_COLORS.UPCOMING
                          )}
                        >
                          {uc.status}
                        </span>
                        <button
                          onClick={() => handleDeleteUndercard(uc.id, uc.title)}
                          className="p-1 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-colors"
                          title="Delete undercard"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <Link href={`/admin/main-events/${eventId}/undercards/${uc.id}`}>
                          <ChevronRight className="h-4 w-4 text-zinc-500 group-hover:text-zinc-300 transition-colors" />
                        </Link>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar — Quick Stats */}
        <div className="space-y-6">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h3 className="text-sm font-medium text-zinc-400 mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-zinc-500">Status</span>
                <span
                  className={cn(
                    'px-2 py-0.5 rounded text-xs font-bold border',
                    STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING
                  )}
                >
                  {event.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-500">Entries</span>
                <span className="text-sm text-white font-medium">{event.entryCount ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-500">Rounds</span>
                <span className="text-sm text-white font-medium">{undercards.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-500">Entry Fee</span>
                <span className="text-sm text-white font-medium">
                  {event.entryFee === 0 ? 'Free' : `${event.entryFee} coins`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-500">Access</span>
                <span className={cn(
                  'text-sm font-medium',
                  event.accessType === 'CODE_REQUIRED' ? 'text-amber-400' : 'text-white'
                )}>
                  {event.accessType === 'CODE_REQUIRED' ? 'Code Required' : 'Open'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-500">Sport</span>
                <span className="text-sm text-white font-medium">{event.sport.replace(/_/g, ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-500">Prize Pool</span>
                <span className="text-sm text-white font-medium">
                  {event.prizePool > 0 ? `${event.prizePool.toLocaleString()} coins` : 'None'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h3 className="text-sm font-medium text-zinc-400 mb-4">Prizes by Placement</h3>
            <div className="space-y-2">
              {prizes.map((prize, i) => (
                <div key={i} className="p-2 rounded-lg bg-zinc-800/50 border border-zinc-700/50 space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      value={prize.placement}
                      onChange={(e) => setPrizes((prev) => prev.map((p, j) => j === i ? { ...p, placement: parseInt(e.target.value) || 1 } : p))}
                      min={1}
                      className="w-12 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-white text-xs focus:border-red-500 focus:outline-none"
                    />
                    <select
                      value={prize.prizeType}
                      onChange={(e) => setPrizes((prev) => prev.map((p, j) => j === i ? { ...p, prizeType: e.target.value as 'COINS' | 'ITEM' } : p))}
                      className="w-20 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-white text-xs focus:border-red-500 focus:outline-none"
                    >
                      <option value="COINS">Coins</option>
                      <option value="ITEM">Item</option>
                    </select>
                    {prize.prizeType === 'COINS' ? (
                      <input
                        type="number"
                        value={prize.coinAmount}
                        onChange={(e) => setPrizes((prev) => prev.map((p, j) => j === i ? { ...p, coinAmount: parseInt(e.target.value) || 0 } : p))}
                        min={0}
                        placeholder="Amount"
                        className="flex-1 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-white text-xs focus:border-red-500 focus:outline-none"
                      />
                    ) : (
                      <input
                        type="text"
                        value={prize.itemName}
                        onChange={(e) => setPrizes((prev) => prev.map((p, j) => j === i ? { ...p, itemName: e.target.value } : p))}
                        placeholder="Item name"
                        className="flex-1 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-white text-xs focus:border-red-500 focus:outline-none"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setPrizes((prev) => prev.filter((_, j) => j !== i))}
                      className="p-1 text-zinc-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                  {prize.prizeType === 'ITEM' && (
                    <div className="space-y-1">
                      <div className="flex gap-2 items-center">
                        <label className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 text-white text-xs cursor-pointer transition-colors shrink-0">
                          <Upload className="h-3 w-3" />
                          Upload
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const { url } = await uploadAdminImage(file, 'prizes');
                                setPrizes((prev) => prev.map((p, j) => j === i ? { ...p, itemImageUrl: url } : p));
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'Upload failed');
                              }
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <input
                          type="url"
                          value={prize.itemImageUrl}
                          onChange={(e) => setPrizes((prev) => prev.map((p, j) => j === i ? { ...p, itemImageUrl: e.target.value } : p))}
                          placeholder="...or paste URL"
                          className="flex-1 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-white text-xs placeholder-zinc-500 focus:border-red-500 focus:outline-none"
                        />
                      </div>
                      {prize.itemImageUrl && (
                        <div>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={prize.itemImageUrl}
                            alt="Prize preview"
                            className="w-8 h-8 rounded object-cover border border-zinc-700"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const next = prizes.length > 0 ? Math.max(...prizes.map((p) => p.placement)) + 1 : 1;
                  setPrizes((prev) => [...prev, { placement: next, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' }]);
                }}
                className="text-xs text-zinc-400 hover:text-white transition-colors flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Add prize
              </button>
              {prizes.filter((p) => p.prizeType === 'COINS').length > 0 && (
                <div className="border-t border-zinc-700 pt-2 mt-2 flex justify-between text-xs">
                  <span className="text-zinc-400">Coin total</span>
                  <span className="text-amber-400 font-medium">
                    {prizes.filter((p) => p.prizeType === 'COINS').reduce((s, p) => s + p.coinAmount, 0).toLocaleString()} coins
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h3 className="text-sm font-medium text-zinc-400 mb-4">Settlement Checklist</h3>
            <div className="space-y-2">
              {undercards
                .sort((a, b) => a.round - b.round)
                .map((uc) => (
                  <div key={uc.id} className="flex items-center gap-2 text-sm">
                    {uc.status === 'COMPLETED' ? (
                      <Check className="h-4 w-4 text-green-400" />
                    ) : (
                      <div className="h-4 w-4 rounded border border-zinc-600" />
                    )}
                    <span className={uc.status === 'COMPLETED' ? 'text-zinc-400 line-through' : 'text-white'}>
                      {uc.title}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Item Prize Claims */}
          {prizeClaims.length > 0 && (
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
              <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Item Prize Claims
              </h3>
              <div className="space-y-3">
                {prizeClaims.map((claim) => (
                  <div key={claim.id} className="p-3 rounded-lg bg-zinc-800 border border-zinc-700 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white font-medium">#{claim.rank} — {claim.user.username}</p>
                        <p className="text-xs text-zinc-400">{claim.mainEventPrize.itemName || 'Item prize'}</p>
                      </div>
                      <span className={cn(
                        'px-2 py-0.5 rounded text-[10px] font-bold border',
                        claim.status === 'UNCLAIMED' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                        claim.status === 'CLAIMED' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                        'bg-green-500/10 text-green-400 border-green-500/30'
                      )}>
                        {claim.status}
                      </span>
                    </div>

                    {claim.status === 'CLAIMED' && (
                      <>
                        <div className="text-xs text-zinc-400 space-y-0.5 border-t border-zinc-700 pt-2">
                          <p>{claim.shippingName}</p>
                          <p>{claim.addressLine1}</p>
                          <p>{claim.city}, {claim.state} {claim.zip}</p>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={trackingInputs[claim.id] || ''}
                            onChange={(e) => setTrackingInputs((prev) => ({ ...prev, [claim.id]: e.target.value }))}
                            placeholder="Tracking number"
                            className="flex-1 px-2 py-1 rounded bg-zinc-900 border border-zinc-700 text-white text-xs placeholder-zinc-500 focus:border-red-500 focus:outline-none"
                          />
                          <button
                            onClick={() => handleShipPrize(claim.id)}
                            className="flex items-center gap-1 px-2 py-1 rounded bg-green-600 hover:bg-green-500 text-white text-xs font-medium transition-colors"
                          >
                            <Truck className="h-3 w-3" />
                            Ship
                          </button>
                        </div>
                      </>
                    )}

                    {claim.status === 'SHIPPED' && claim.trackingNumber && (
                      <div className="text-xs text-green-400 border-t border-zinc-700 pt-2 flex items-center gap-1">
                        <Truck className="h-3 w-3" />
                        {claim.trackingNumber}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
